import { Injectable } from '@angular/core';

@Injectable()
export class SortingService {
  sort<T>(
    list: readonly T[] | null | undefined,
    propertyName: string,
    direction: 'asc' | 'desc' = 'asc',
    caseSensitive = false
  ): T[] {
    if (!list) {
      return [];
    }
    if (!propertyName?.trim()) {
      return [...list];
    }

    const keepOnTop: T[] = [];
    const rest: T[] = [];

    for (const item of list) {
      if (this.hasKeepOnTop(item)) {
        keepOnTop.push(item);
      } else {
        rest.push(item);
      }
    }

    const sorted = [...rest].sort((item1, item2) => {
      const a = this.getProperty(item1, propertyName);
      const b = this.getProperty(item2, propertyName);

      return direction === 'asc'
        ? this.sortByComparator(a, b, caseSensitive)
        : this.sortByComparator(b, a, caseSensitive);
    });

    return [...keepOnTop, ...sorted];
  }

  private hasKeepOnTop(obj: unknown): boolean {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      'keepOnTop' in obj &&
      Boolean((obj as { keepOnTop?: unknown }).keepOnTop)
    );
  }

  private sortByComparator(
    a: unknown,
    b: unknown,
    caseSensitive: boolean
  ): number {
    if (a == null && b == null) {
      return 0;
    }
    if (a == null) {
      return -1;
    }
    if (b == null) {
      return 1;
    }

    const aNum = Number(a);
    const bNum = Number(b);

    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
      return aNum - bNum;
    }

    const aStr = String(a);
    const bStr = String(b);

    return caseSensitive
      ? aStr.localeCompare(bStr)
      : aStr.localeCompare(bStr, undefined, { sensitivity: 'accent' });
  }

  private getProperty(obj: unknown, path: string): unknown {
    return path.split('.').reduce<unknown>((result, prop) => {
      if (result && typeof result === 'object') {
        return (result as Record<string, unknown>)[prop];
      }
      return undefined;
    }, obj);
  }
}
